import { Skeleton, Typography, Button, Modal } from '@mui/material';
import { Paper } from '@mui/material';
import { useTheme } from '@mui/material';

import { Box } from '@mui/material';

import Iconify from 'src/components/iconify';

import { useDispatch, useSelector } from 'react-redux';
import { setAdvancedView } from 'src/context/reducers/filter-settings/filterSlice';
import { Tabs, Tab, ButtonGroup } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { RootState } from 'src/context/store';
import { GridFilterModel } from '@mui/x-data-grid';
import { GridSortModel } from '@mui/x-data-grid';
import { useDataQuery, useKpisQuery } from 'src/context/api/data/api';
import { useExportMutation } from 'src/context/api/exports/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import { Icon } from '@iconify/react';
import GenericDataGrid from 'src/components/data-grid/generic-data-grid';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

import 'src/components/data-grid/data-grid.scss';
import { enqueueSnackbar } from 'notistack';

export default function TabulableTable() {
  const { t } = useLocales();
  const theme = useTheme();
  const dispatch = useDispatch();
  const workspace = useCurrentWorkspace();
  const filterStore = useSelector((state: RootState) => state.filters);
  const loadedPlatforms = useSelector((state: RootState) => state.filters.loadedPlatform);
  const mode = useSelector((state: RootState) => state.filters.extraOptions.mode);
  const [value, setValue] = useState(0);
  const isAdvancedView = useSelector((state: RootState) => state.filters.isAdvancedView);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field:
        value === 3
          ? filterStore?.timestep.toLowerCase().replace('date_day', 'date')
          : 'brand_searches',
      sort: 'desc',
    },
  ]);
  const [filters, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [exportMutation] = useExportMutation();
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({});


const tabs = {
    base: [
      {
        label: 'dashboard.global.keywords',
        value: 0,
        groupby: ['search_term'],
      },
      {
        label: 'dashboard.global.countries',
        value: 1,
        groupby: ['region_name'],
      },
      {
        label: 'dashboard.global.devices',
        value: 2,
        groupby: ['device_type'],
      },
      {
        label: 'dashboard.global.date',
        value: 3,
        groupby: [filterStore?.timestep.toLowerCase().replace('date_day', 'date')],
      },
    ],
  };

  useEffect(() => {
    if (
      loadedPlatforms.length > 1 &&
      !tabs[mode as keyof typeof tabs].some((tab) => tab.value === 8)
    ) {
      tabs[mode as keyof typeof tabs].push({
        label: 'dashboard.filters.platforms',
        value: 8,
        groupby: ['platform'],
      });
    } else {
      tabs[mode as keyof typeof tabs] = tabs[mode as keyof typeof tabs].filter(
        (tab) => tab.value !== 8
      );
    }
  }, [loadedPlatforms]);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);

    // Reset du sort model lors du changement de tab pour éviter les problèmes de validation
    if (newValue === 3) {
      setSortModel([
        { field: filterStore?.timestep.toLowerCase().replace('date_day', 'date'), sort: 'desc' },
      ]);
    } else {
      const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
      setSortModel([{ field: defaultField, sort: 'desc' }]);
    }

    // Reset aussi les filtres si nécessaire
    setFilterModel({
      items: [],
    });
  };

  const { data: kpis, isLoading: isFetchingKPIs, error: kpisError } = useKpisQuery();

  const order_by = useMemo(() => {
    if (!sortModel.length) return [];

    return sortModel
      .map(({ field, sort }) => {
        const compareMatch = field.match(/^(.*?)(Compare|Diff)$/);
        if (compareMatch) {
          const baseField = compareMatch[1].toLowerCase();
          const suffix = compareMatch[2];
          if (suffix === 'Compare') return [`compare.${baseField}`, sort];
          if (suffix === 'Diff') return [`diff.${baseField}`, sort];
        }

        const kpi = kpis?.find((k) => k.id === field);

        if (!kpi) return null;

        return [field, sort];
      })
      .filter((item) => !!item) as [string, string][];
  }, [sortModel, kpis]);

  const groupby = useMemo(
    () => tabs[mode as keyof typeof tabs].find((tab) => tab.value === value)?.groupby ?? [],
    [tabs, value, mode]
  );

  const defaultSortModelForTab = useMemo<GridSortModel>(() => {
    if (value === 3) {
      return [
        {
          field: filterStore?.timestep?.toLowerCase().replace('date_day', 'date') ?? 'date',
          sort: 'desc',
        },
      ];
    }
    const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
    return [{ field: defaultField, sort: 'desc' }];
  }, [value, mode, filterStore?.timestep]);

  const queryOpts = useMemo(
    () => ({
      group_by: groupby,
      comparison: filterStore?.compare ? 'true' : 'false',
      start: filterStore?.start,
      end: filterStore?.end,
      interval: filterStore?.timestep.toLowerCase(),
      page_size: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search_terms: filterStore?.keywords ?? [],
      countries: filterStore?.countries ?? [],
      devices: filterStore?.devices ?? [],
      platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
      start_compare: filterStore?.compare ? filterStore?.compareStart : undefined,
      end_compare: filterStore?.compare ? filterStore?.compareEnd : undefined,
      mode: 'base' as 'base' | 'activations',
      output: 'table' as 'table' | 'charts',
      filters,
      workspace_id: workspace?.id as number,
      extra_options: filterStore?.extraOptions,
      order_by,
      tags: filterStore?.tags?.map((tag: any) => tag.label) ?? [],
    }),
    [
      groupby,
      filterStore?.compare,
      filterStore?.start,
      filterStore?.end,
      filterStore?.timestep,
      paginationModel.pageSize,
      paginationModel.page,
      filterStore?.keywords,
      filterStore?.countries,
      filterStore?.devices,
      filterStore?.platforms,
      filterStore?.compareStart,
      filterStore?.compareEnd,
      filterStore?.extraOptions,
      order_by,
      filterStore?.tags,
    ]
  );

  const {
    data: {
      data: tableData,
      total_rows = 0,
      total_pages = 0,
      current_page = 0,
      page_size = 0,
      query_job_id: queryID,
    } = {},
    isLoading: isLoadingTableData,
    isFetching: isFetchingTableData,
    error: tableDataError,
  } = useDataQuery(queryOpts, {
    skip:
      !filterStore?.timestep ||
      !filterStore?.compareStart ||
      !filterStore?.compareEnd ||
      !kpis?.length ||
      groupby.length === 0,
  });

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? '#212b36' : 'white',
        boxShadow:
          '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        minHeight: 700,
        borderRadius: 2,
        marginTop: 3,
        marginBottom: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          pt: 2,
          mb: 1,
          color: 'rgba(118, 85, 193, 1)',
        }}
      >
        <Tabs
          TabIndicatorProps={{
            sx: {
              backgroundColor: 'rgba(118, 85, 193, 1)',
              height: '2px',
            },
          }}
          value={value}
          onChange={handleChange}
          aria-label={t('dashboard.brandTraffic.basic_tabs_example')}
        >
          {tabs[mode as keyof typeof tabs].map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.label)} />
          ))}
        </Tabs>
        <ButtonGroup
          size="small"
          aria-label="contained primary button group"
          sx={{
            border: '1px solid rgba(226, 229, 233, 0.16)',
            borderRadius: 1,
            padding: '3px',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              size: '12px',
              pl: 1,
              pr: 1,
              color: (theme) =>
                theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300],
            }}
          >
            {t('dashboard.global.advanced')}
          </Typography>
          {[
            {
              label: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Icon
                    icon="material-symbols:grid-off-rounded"
                    style={{
                      fontSize: '14px',
                      color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined,
                    }}
                  />
                </Box>
              ),
              isActive: !isAdvancedView,
            },
            {
              label: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Icon
                    icon="bi:grid-3x3-gap-fill"
                    style={{
                      fontSize: '14px',
                      color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined,
                    }}
                  />
                </Box>
              ),
              isActive: isAdvancedView,
            },
          ].map(({ label, isActive }, index) => (
            <Button
              key={typeof label === 'string' ? label : 'Advanced'}
              variant={isActive ? 'contained' : 'outlined'}
              onClick={() => dispatch(setAdvancedView(index === 1))}
              sx={{
                ...(isActive && {
                  border: 0,
                  backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                  color: (theme) => theme.palette.primary.light,
                  borderRadius: 3,
                  borderTopRightRadius: index === 0 ? 0 : 3,
                  borderBottomRightRadius: index === 0 ? 0 : 3,
                  borderTopLeftRadius: index === 1 ? 0 : 3,
                  borderBottomLeftRadius: index === 1 ? 0 : 3,
                  '&:hover': {
                    backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                    border: 0,
                    boxShadow: 'none',
                  },
                }),
                ...(!isActive && {
                  border: 0,
                  borderRadius: 3,
                  borderTopRightRadius: index === 0 ? 0 : 3,
                  borderBottomRightRadius: index === 0 ? 0 : 3,
                  borderTopLeftRadius: index === 1 ? 0 : 3,
                  borderBottomLeftRadius: index === 1 ? 0 : 3,
                  color: (theme) => theme.palette.grey[300],
                  '&:hover': {
                    backgroundColor: 'transparent',
                    border: 0,
                    boxShadow: 'none',
                  },
                }),
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      <Box>
        {isFetchingTableData || (!tableData && !tableDataError) ? (
          <Skeleton
            width={'100%'}
            height={680}
            style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}
            variant="rectangular"
            animation={'wave'}
          >
            <Iconify py={'auto'} icon={'eva:checkmark-circle-2-fill'} color={'success.main'} />
            <Typography color={'success.main'}>Active</Typography>
          </Skeleton>
        ) : (
          tableData && (
            <Box height={'auto'}>
              <GenericDataGrid
                pinnedColumns={groupby ?? []}
                data={tableData as any}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                rowCount={total_rows}
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  if (model.length === 0) {
                    setSortModel(defaultSortModelForTab);
                  } else {
                    setSortModel(model as GridSortModel);
                  }
                }}
                filterModel={filters}
                onFilterModelChange={setFilterModel}
                onVisibilityModelChange={setVisibilityModel}
                visibilityModel={visibilityModel}
                onExport={(columns) => {
                  exportMutation({
                    queryId: queryID as string,
                    name: `[${workspace?.name}] Traffic details export`,
                    columns: Object.keys(visibilityModel).filter(
                      (key) => visibilityModel[key as keyof typeof visibilityModel] !== false
                    ),
                    column_tr: Object.keys(visibilityModel).reduce(
                      (acc, key) => {
                        acc[key] = columns.find((column) => column.field === key)
                          ?.headerName as string;
                        return acc;
                      },
                      {} as Record<string, string>
                    ),
                    dimensions: groupby,
                  })
                    .unwrap()
                    .then((res) => {
                      enqueueSnackbar(
                        "Export is running in background, you will be notified when it's done",
                        { variant: 'success' }
                      );
                    })
                    .catch((err) => {
                      enqueueSnackbar('Error exporting data', { variant: 'error' });
                    });
                }}
              />
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
}
